import { ReactNode } from "react";
import { isTaskTerminatingStatus, Task, TaskType } from "@common/types/task";
import { BrokenConnectionCommandIsland } from "./BrokenConnection/BrokenConnectionCommandIsland";
import { ConnectAccountCommandIsland } from "./ConnectAccount/ConnectAccountCommandIsland";
import { IntegrationCoaMappingCommandIsland } from "./IntegrationCoaMapping/IntegrationCoaMappingCommandIsland";
import { InviteAccountantCommandIsland } from "./InviteAccountant/InviteAccountantCommandIsland";
import { MapChartOfAccountsCommandIsland } from "./MapChartOfAccounts/MapChartOfAccountsCommandIsland";
import { MapMigratedDataCommandIsland } from "./MapMigratedData/MapMigratedDataCommandIsland";
import { PlaidNewAccountsCommandIsland } from "./PlaidNewAccounts/PlaidNewAccountsCommandIsland";
import { ReceiptPhoneNumberCommandIsland } from "./ReceiptPhoneNumber/ReceiptPhoneNumberCommandIsland";
import { StatementsRefreshCommandIsland } from "./StatementsRefresh/StatementsRefreshCommandIsland";
import { HAS_COMMAND_ISLAND_BY_TASK_TYPE } from "./TaskBodyCommandIsland.lib";

interface TaskBodyCommandIslandProps {
    selectedTask: Task;
}

type CommandIslandRenderer = (task: Task) => ReactNode;

type CommandIslandRenderers = {
    [T in TaskType]: (typeof HAS_COMMAND_ISLAND_BY_TASK_TYPE)[T] extends true
        ? CommandIslandRenderer
        : null;
};

const COMMAND_ISLAND_BY_TASK_TYPE: CommandIslandRenderers = {
    [TaskType.INVITE_ACCOUNTANT]: () => <InviteAccountantCommandIsland />,
    [TaskType.RECEIPT_PHONE_NUMBER]: () => <ReceiptPhoneNumberCommandIsland />,
    [TaskType.CONNECTION_BROKEN]: (task) => (
        <BrokenConnectionCommandIsland selectedTask={task} />
    ),
    [TaskType.PLAID_NEW_ACCOUNTS_AVAILABLE]: (task) => (
        <PlaidNewAccountsCommandIsland selectedTask={task} />
    ),
    [TaskType.PLAID_STATEMENTS_REFRESH]: (task) => (
        <StatementsRefreshCommandIsland selectedTask={task} />
    ),
    [TaskType.MAP_CHART_OF_ACCOUNTS]: (task) => (
        <MapChartOfAccountsCommandIsland selectedTask={task} />
    ),
    [TaskType.CONNECT_ACCOUNT]: (task) => (
        <ConnectAccountCommandIsland selectedTask={task} />
    ),
    [TaskType.MAP_MIGRATED_DATA]: (task) =>
        isTaskTerminatingStatus(task.status) ? null : (
            <MapMigratedDataCommandIsland selectedTask={task} />
        ),
    [TaskType.INTEGRATION_COA_MAPPING]: (task) =>
        isTaskTerminatingStatus(task.status) ? null : (
            <IntegrationCoaMappingCommandIsland selectedTask={task} />
        ),
    [TaskType.FILE]: null,
    [TaskType.TRANSACTION]: null,
    [TaskType.TRANSACTIONS]: null,
    [TaskType.QUESTION]: null,
    [TaskType.PROFIT_LOSS_ACTIVATION]: null,
    [TaskType.PROFIT_LOSS]: null,
    [TaskType.BALANCE_SHEET]: null,
    [TaskType.DOCUMENT_RETRIEVAL]: null,
    [TaskType.GET_DOCUMENTS]: null,
    [TaskType.ACCRUAL_REVIEW]: null,
    [TaskType.RECONCILIATION]: null,
    [TaskType.FILE_MIGRATION_UPLOAD]: null,
    [TaskType.XERO_FILE_UPLOAD]: null,
    [TaskType.QBDT_FILE_UPLOAD]: null,
    [TaskType.TRANSACTION_REVIEW]: null,
    [TaskType.FORM_1099]: null,
    [TaskType.COUNTERPARTIES]: null,
    [TaskType.BILLS]: null,
    [TaskType.INVOICES]: null,
    [TaskType.FINANCIAL_DOCUMENTS]: null,
    [TaskType.SCHEDULE_RULE_SUGGESTED_REVIEW]: null,
    [TaskType.SCHEDULE_SUSPICIOUS_TRANSACTION_REVIEW]: null,
    [TaskType.REVENUE_RECOGNITION_REVIEW]: null,
    [TaskType.SCHEDULE_SOURCE_CHANGE_REVIEW]: null,
    [TaskType.MILESTONE_REVENUE_RECOGNITION_REVIEW]: null,
};

export const TaskBodyCommandIsland = ({
    selectedTask,
}: TaskBodyCommandIslandProps) => {
    const renderCommandIsland = COMMAND_ISLAND_BY_TASK_TYPE[selectedTask.type];
    return renderCommandIsland ? renderCommandIsland(selectedTask) : null;
};
